import { useMsal } from "@azure/msal-react";
import { useLocation, useSearchParams } from "react-router-dom";
import { useEffect, useRef } from "react";
import { loginRequest } from "../authConfig";
import { useCookies } from "react-cookie";

export const Authenticate = () => {
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const [, setRedirectUrlCookie] = useCookies(["redirectData"]);

  const { instance, inProgress } = useMsal();
  const activeAccount = instance.getActiveAccount();
  const hasTriggeredLogin = useRef(false);

  // If user is already authenticated, redirect them
  useEffect(() => {
    if (activeAccount && inProgress === "none") {
      const from = (location.state as any)?.from || "/";
      window.location.href = from;
    }
  }, [activeAccount, inProgress, location.state]);

  // Handle external app login requests (with URL parameters)
  useEffect(() => {
    if (searchParams && searchParams.size === 2 ) {
      const type = searchParams.get("type");
      const redirectUrl: any = searchParams.get("redirectUrl");

      if (type && type === "login" && inProgress === "none") {
        setRedirectCookiees(redirectUrl, type);
        loginForApp();
      } else if (type && type === "logout") {
        localStorage.clear();
        instance.logoutRedirect();
      } else if (type && type === "resetPass" && inProgress === "none") {
        setRedirectCookiees(redirectUrl, type);
        openResetPassword();
      }
    }
  }, [searchParams, instance, inProgress]);

  // Handle session expiry and inactivity login - automatically trigger login when no account exists
  useEffect(() => {
    const sessionExpired = searchParams.get("sessionExpired");
    const inactivity = searchParams.get("inactivity");
    const hasSessionIssue = sessionExpired === "true" || inactivity === "true";
    
    // Only trigger if:
    // 1. No active account
    // 2. MSAL is not in progress
    // 3. Session expired/inactivity flag is present OR no account and no search params (normal login)
    // 4. Not already handling external app login
    // 5. Haven't already triggered login
    if (
      !activeAccount &&
      inProgress === "none" &&
      (hasSessionIssue || (!searchParams || searchParams.size === 0)) &&
      !searchParams?.get("type") &&
      !hasTriggeredLogin.current
    ) {
      hasTriggeredLogin.current = true;
      
      console.log("🔄 Triggering login redirect", { sessionExpired, inactivity });
      
      // Add timeout to prevent hanging
      let timeoutId: NodeJS.Timeout | null = null;
      let loginTriggered = false;
      
      const triggerLogin = () => {
        if (loginTriggered) {
          console.log("Login already triggered, skipping");
          return;
        }
        loginTriggered = true;
        if (timeoutId) clearTimeout(timeoutId);
        
        console.log("🚀 Triggering loginRedirect with prompt: login");
        instance.loginRedirect({
          ...loginRequest,
          prompt: "login", // Force interactive login, don't use cached credentials
        }).catch((error) => {
          console.error("Login redirect error:", error);
          hasTriggeredLogin.current = false; // Reset on error to allow retry
          loginTriggered = false;
        });
      };
      
      timeoutId = setTimeout(() => {
        if (hasTriggeredLogin.current && !loginTriggered) {
          console.warn("⏰ Login redirect timeout - forcing direct login");
          triggerLogin();
        }
      }, 3000); // 3 second timeout
      
      // Handle any pending redirect promise first
      instance.handleRedirectPromise()
        .then(() => {
          if (!loginTriggered) {
            console.log("✅ handleRedirectPromise completed, triggering login");
            triggerLogin();
          }
        })
        .catch((error) => {
          if (!loginTriggered) {
            console.warn("⚠️ handleRedirectPromise failed, trying direct login:", error);
            triggerLogin();
          }
        });
      
      // Cleanup function
      return () => {
        if (timeoutId) {
          clearTimeout(timeoutId);
        }
      };
    }
    
    // Reset the flag if account becomes available (login successful)
    if (activeAccount) {
      hasTriggeredLogin.current = false;
    }
  }, [activeAccount, inProgress, searchParams, instance]);

  const openResetPassword = () => {
    const authority = process.env.REACT_APP_AZURE_RESET_PASSWORD_AUTHORITY;
    instance.loginRedirect({
      authority: authority,
      scopes: ["offline_access"],
    });
  };

  const setRedirectCookiees = async (redirectUrl: string, type: string) => {
    setRedirectUrlCookie("redirectData", { redirectUrl, type }, { path: "/" });
  };

  const loginForApp = async () => {
    await instance.handleRedirectPromise();
    instance.loginRedirect(loginRequest).catch((error) => {
      console.error("Login error:", error);
    });
  };

  return (
    <div
      style={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "100vh",
        backgroundColor: "#f5f5f5",
      }}
    >
      <div style={{ textAlign: "center" }}>
        <h2 style={{ color: "#333", marginBottom: "20px" }}>Redirecting...</h2>
        <div
          style={{
            border: "4px solid #f3f3f3",
            borderTop: "4px solid #0078d4",
            borderRadius: "50%",
            width: "40px",
            height: "40px",
            animation: "spin 1s linear infinite",
            margin: "0 auto",
          }}
        ></div>
        <style>{`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}</style>
      </div>
    </div>
  );
};
